import "./queueFleet.css";
import { api, ApiError } from "../api";
import { emptyBlock, errorBanner, loadingBlock, pill, tableRegion } from "./shared/blocks";
import { h, tidy } from "./shared/safe";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { FLEET_MAX_LIMIT, fleetQuery, parseClients, parsePage, type FleetClient } from "./fleetModel";

type Publication = { platform: string; destinationUrl: string; state: string; lastUpdatedAt: string | null };
type Website = { websiteId: string; siteId?: string; type: string; siteKind: string; template?: string | null; destinationUrl?: string; platform?: string | null; state: string; removedAt?: string | null; latestRevision?: number; currentReleaseNumber?: number; lastUpdatedAt?: string | null; publications?: Publication[] };
type WebsiteRow = { client: FleetClient; website: Website };
type Kit = { kitId: string; title: string; family?: string };
const enc = encodeURIComponent;
const KEY = "cloudpush_websites_state";
let memoryState = { query: "", page: 0 };
function restoreState() {
  try { const saved = sessionStorage.getItem(KEY); if (saved) { const parsed = JSON.parse(saved); if (typeof parsed.query === "string" && Number.isInteger(parsed.page) && parsed.page >= 0) memoryState = parsed; } } catch { /* keep the in-memory state */ }
  return { ...memoryState };
}
function saveState(next: { query: string; page: number }) {
  memoryState = next;
  try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* keep the in-memory state */ }
}
function statusFor(site: Website): string {
  if (site.removedAt || site.state === "removed") return "Removed · locked";
  const states = (site.publications ?? []).map((p) => p.state.toLowerCase());
  if (states.includes("delivered_uninspected")) return "Delivery recorded · inspection pending";
  if (states.some((s) => ["partial", "partially_delivered"].includes(s))) return "Partially delivered";
  if (states.some((s) => ["failed", "dead"].includes(s))) return "Publication failed";
  if (states.some((s) => ["queued", "pending", "publishing", "running", "needs_reconciliation", "unknown"].includes(s))) return "Pending publication";
  if (states.some((s) => ["live", "published", "delivered", "observed", "active"].includes(s))) return site.state === "draft" ? "Published · draft changes" : "Published";
  if (site.state === "draft") return "Draft";
  if (site.state === "released") return "Release saved · publication not confirmed";
  const state = tidy(site.state.replaceAll("_", " "), 50);
  return state ? state[0]!.toUpperCase() + state.slice(1) : "Status unavailable";
}
function typeFor(site: Website, kits: Kit[]): string {
  if (site.type !== "kit" || !site.template) return "Unknown website type";
  const family = kits.find((kit) => kit.kitId === site.template)?.family;
  const labels: Record<string, string> = { profile: "Business profile", blog: "Blog", directory: "Directory", wiki: "Wiki", service_hub: "Service and location guide", resource: "Magazine / resource hub", case_study: "Case studies" };
  return family ? labels[family] ?? "Unknown website type" : "Unknown website type";
}
function displayName(site: Website): string {
  if (site.type === "legacy" && typeof site.websiteId === "string") return site.websiteId;
  const raw = site.destinationUrl ?? "";
  try { return new URL(raw).hostname; } catch { return raw || "Website address unavailable"; }
}

/** Fleet index uses the combined per-client website read model and deliberately does not fetch per-site histories here. */
export async function websitesView(root: HTMLElement): Promise<void> {
  const shell = h("section", { class: "qf cp-websites" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  const state = restoreState();
  let error: string | null = null;
  let rows: WebsiteRow[] | null = null;
  let kits: Kit[] = [];
  const head = h("div", { class: "topbar" }, h("div", null, h("h1", null, "Websites"), h("p", { class: "muted" }, "Find each client website, its provider and its current recorded status.")), h("a", { class: "btn", href: "#/templates" }, "Choose a website kit"));
  const note = h("div", { class: "cp-page-note", role: "note" }, "Saved websites stay independent. Provider publication status is shown separately from a locally saved draft or release.");
  const content = h("div", { class: "cp-websites__body" });
  shell.append(head, note, content);

  const render = () => {
    if (error) return void content.replaceChildren(errorBanner(error, { retry: () => void load() }));
    if (rows === null) return void content.replaceChildren(loadingBlock("Loading website workspaces", 5));
    let filtered = filterRows(rows, state.query);
    const pageSize = 12;
    const maxPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
    state.page = Math.min(state.page, maxPage);
    saveState(state);
    const search = h("input", { type: "search", placeholder: "Search websites, clients or providers", "aria-label": "Search websites", autocomplete: "off", value: state.query, on: { input: (e: Event) => { state.query = (e.target as HTMLInputElement).value; state.page = 0; saveState(state); drawResults(); } } }) as HTMLInputElement;
    const count = h("p", { class: "qf-summary", role: "status", "aria-live": "polite" });
    const results = h("div", { class: "cp-websites__results" });
    const drawResults = () => {
      filtered = filterRows(rows!, state.query);
      const actualMax = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
      state.page = Math.min(state.page, actualMax);
      const start = state.page * pageSize;
      const visible = filtered.slice(start, start + pageSize);
      count.textContent = `${visible.length ? `${start + 1}–${start + visible.length} of ` : ""}${filtered.length} ${filtered.length === 1 ? "website" : "websites"}${state.query.trim() ? " match your search" : ""}.`;
      if (!filtered.length) return void results.replaceChildren(emptyBlock(rows!.length ? "No websites match" : "No websites yet", rows!.length ? "Try another search." : "Create a site from a client's website workspace or choose a kit.", rows!.length ? [] : [h("a", { class: "btn", href: "#/templates" }, "Browse website kits")]));
      const table = h("table", { class: "qf-table cp-websites__table" },
        h("caption", { class: "qf-sr-only" }, "Client websites"),
        h("thead", null, h("tr", null, ...["Website / client", "Website type", "Platform", "Status", "Updated", "Workspace"].map((t) => h("th", { scope: "col" }, t)))),
        h("tbody", null, ...visible.map(({ website, client }) => {
          const siteId = website.siteId ?? website.websiteId;
          const href = `#/clients/${enc(client.id ?? "")}/site?siteId=${enc(siteId)}`;
          const status = statusFor(website);
          const publications = website.publications ?? [];
          const platforms = [...new Set([website.platform, ...publications.map((p) => p.platform)].filter((p): p is string => !!p))];
          return h("tr", null,
            h("td", null, h("a", { class: "qf-business qf-wrap", href }, tidy(displayName(website), 180)), h("div", { class: "muted qf-wrap" }, `${tidy(client.name, 120)} · ${website.siteKind === "independent" ? "separate website" : website.siteKind === "satellite" ? "satellite website" : "main website"}`), website.destinationUrl ? h("div", { class: "muted qf-wrap" }, tidy(website.destinationUrl, 240)) : null),
            h("td", null, typeFor(website, kits)),
            h("td", null, platforms.length ? platforms.map((p) => h("div", null, tidy(p, 64))) : "Platform not recorded"),
            h("td", null, pill(status, status.startsWith("Published") ? "ok" : status.includes("Removed") ? "neutral" : status.includes("Draft") || status.includes("Pending") ? "warn" : "neutral")),
            h("td", null, website.lastUpdatedAt ? tidy(website.lastUpdatedAt, 36) : "Time unavailable"),
            h("td", null, h("a", { class: "qf-open", href }, website.type === "legacy" ? "View details →" : "Manage website →")),
          );
        })),
      );
      const pager = h("div", { class: "qf-pagination", role: "group", "aria-label": "Website pages" },
        h("button", { type: "button", class: "secondary", disabled: state.page === 0, on: { click: () => { state.page--; saveState(state); drawResults(); } } }, "Previous"),
        h("span", { class: "muted" }, `Page ${state.page + 1} of ${actualMax + 1}`),
        h("button", { type: "button", class: "secondary", disabled: state.page >= actualMax, on: { click: () => { state.page++; saveState(state); drawResults(); } } }, "Next"),
      );
      results.replaceChildren(tableRegion("Client websites", table), pager);
    };
    content.replaceChildren(h("div", { class: "cp-websites__toolbar" }, search), count, results);
    drawResults();
  };

  async function load() {
    const req = life.begin(); error = null; rows = null; render();
    try {
      const clients: FleetClient[] = [];
      let offset = 0;
      let complete = false;
      for (let pageNo = 0; pageNo < 100; pageNo += 1) {
        const reply = await api<unknown>(`/v1/clients${fleetQuery("", "all", offset, FLEET_MAX_LIMIT)}`, { signal: req.signal });
        const parsed = parseClients(reply);
        if (!parsed) throw new Error("The server sent a client list this page does not understand.");
        clients.push(...parsed.clients);
        const page = parsePage(reply);
        if (!page || !page.hasMore) { complete = true; break; }
        if (!page.returned) throw new Error("The client list stopped before all pages were read.");
        offset += page.returned;
      }
      if (!complete) throw new Error("The client list exceeded the supported page count.");
      const [kitReply, websiteLists] = await Promise.all([
        api<{ kits?: Kit[] }>("/v1/site-kits?view=summary", { signal: req.signal }).catch(() => ({ kits: [] })),
        readClientWebsitePages(clients.filter((client) => client.id), req.signal),
      ]);
      if (!req.current()) return;
      kits = kitReply.kits ?? [];
      rows = websiteLists.flatMap(({ client, websites }) => websites.map((website) => ({ client, website })));
      render();
    } catch (e) {
      if (!req.current() || isAbort(e)) return;
      error = e instanceof ApiError ? `Website workspaces could not be loaded (HTTP ${e.status}).` : e instanceof Error ? e.message : "Website workspaces could not be loaded.";
      render();
    }
  }
  await load();
}

function filterRows(rows: WebsiteRow[], query: string): WebsiteRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter(({ website, client }) => `${client.name} ${client.slug} ${client.city} ${client.state} ${website.destinationUrl ?? ""} ${website.platform ?? ""} ${website.template ?? ""} ${website.state} ${website.siteKind}`.toLowerCase().includes(q));
}

async function readClientWebsitePages(clients: FleetClient[], signal: AbortSignal): Promise<{ client: FleetClient; websites: Website[] }[]> {
  const result: { client: FleetClient; websites: Website[] }[] = [];
  // No fleet-level list endpoint exists on the reviewed backend. Keep per-client list reads bounded.
  for (let offset = 0; offset < clients.length; offset += 6) {
    const batch = await Promise.all(clients.slice(offset, offset + 6).map(async (client) => {
      const reply = await api<{ websites?: Website[] }>(`/v1/clients/${enc(client.id ?? "")}/websites`, { signal });
      return { client, websites: Array.isArray(reply.websites) ? reply.websites : [] };
    }));
    result.push(...batch);
  }
  return result;
}
